import * as Yup from 'yup';

export type PptBSchemaField = any;

export type PptBExtraFieldsSchemaResponse = {
  fields: PptBSchemaField[];
};

export type PptBNameValue = {
  name: string;
  value: string;
};

/** Fallback when schema API is unavailable (local dev). Matches seeded RJ ppt-b shape. */
export const DUMMY_PPT_B_SCHEMA_FIELDS: PptBSchemaField[] = [
  {
    id: 'job_title',
    type: 'text',
    label: 'Job Title',
    validation: { required: true, maxLength: 50 },
    defaultValue: '',
  },
  {
    id: 'position',
    type: 'text',
    label: 'Position',
    validation: { required: true, maxLength: 50 },
    defaultValue: '',
  },
];

export function normalizePptBSchemaResponse(
  data: PptBSchemaField[] | PptBExtraFieldsSchemaResponse | null | undefined
): PptBExtraFieldsSchemaResponse {
  const raw = Array.isArray(data) ? data : (data?.fields ?? []);
  return { fields: filterPptBRenderableFields(raw) };
}

export function filterPptBRenderableFields(fields: PptBSchemaField[]): PptBSchemaField[] {
  return fields.filter((field) => field.type?.toLowerCase() === 'text');
}

/** Stable Formik key; `name` in submit payload uses schema `id`. */
export function getPartBFormFieldKey(field: PptBSchemaField): string {
  return `partB_${field.id}`;
}

export function getPartBFieldLabel(field: PptBSchemaField): string {
  return field.label ?? field.id;
}

export function getPartBFieldMaxLength(field: PptBSchemaField): number {
  return field.validation?.maxLength ?? 50;
}

export function isPartBFieldRequired(field: PptBSchemaField): boolean {
  return field.validation?.required === true;
}

export function buildPartBExtraFieldsInitialValues(
  fields: PptBSchemaField[]
): Record<string, string> {
  return Object.fromEntries(
    fields.map((field) => [getPartBFormFieldKey(field), field.defaultValue ?? ''])
  );
}

export function mergeSavedExtraPropertiesIntoInitialValues(
  fields: PptBSchemaField[],
  saved: PptBNameValue[] | null | undefined
): Record<string, string> {
  const byName = new Map((saved ?? []).map((item) => [item.name, item.value ?? '']));
  return Object.fromEntries(
    fields.map((field) => [
      getPartBFormFieldKey(field),
      byName.get(field.id) ?? field.defaultValue ?? '',
    ])
  );
}

export function buildPartBExtraFieldsValidationShape(
  fields: PptBSchemaField[]
): Record<string, Yup.StringSchema> {
  const shape: Record<string, Yup.StringSchema> = {};
  for (const field of fields) {
    const key = getPartBFormFieldKey(field);
    const maxLength = getPartBFieldMaxLength(field);
    let schema = Yup.string().nullable();
    if (isPartBFieldRequired(field)) {
      schema = schema.required('Required field');
    }
    if (maxLength > 0) {
      schema = schema.max(maxLength, `Maximum ${maxLength} characters`);
    }
    shape[key] = schema;
  }
  return shape;
}

/** Submit / extension storage: `name` = schema `id`. */
export function buildPartBExtraFieldsPayload(
  values: Record<string, unknown>,
  fields: PptBSchemaField[]
): PptBNameValue[] {
  return fields.map((field) => ({
    name: field.id,
    value:
      values[getPartBFormFieldKey(field)] != null
        ? String(values[getPartBFormFieldKey(field)]).trim()
        : '',
  }));
}

export function omitPartBExtraFieldKeys<T extends Record<string, unknown>>(
  data: T,
  fields: PptBSchemaField[]
): Omit<T, string> {
  const next = { ...data };
  for (const field of fields) {
    delete next[getPartBFormFieldKey(field)];
  }
  delete next.extraProperties;
  return next;
}

export function getPartBFieldDisplayValue(
  stepOneData: Record<string, unknown> | undefined,
  field: PptBSchemaField
): string {
  const key = getPartBFormFieldKey(field);
  const raw = stepOneData?.[key];
  if (raw != null && raw !== '') {
    return String(raw);
  }
  return '-';
}

/** Saved values on participant-information-for-sign-up (array or nested). */
export function extractExtraPropertiesFromSignUp(
  signUp: Record<string, unknown> | null | undefined
): PptBNameValue[] | undefined {
  if (signUp == null) {
    return undefined;
  }
  const raw =
    signUp['extra-properties'] ??
    signUp.extraProperties ??
    signUp.extraPropertiesJson ??
    signUp.pptBExtraProperties;
  if (!Array.isArray(raw)) {
    return undefined;
  }
  return raw.map((item: { name?: string; value?: string | null }) => ({
    name: item.name ?? '',
    value: item.value ?? '',
  }));
}
